import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { formatCST } from '@/lib/time'
import { useIsMobile } from '@/lib/hooks'
import { AdminMobileList } from '@/components/AdminMobileList'
import {
  ROUTE_SOURCE_LABELS,
  rerouteRate,
  splitKeywords,
  type RouteRule,
  type RouteSource,
  type RouteStatRow,
} from '@/lib/feedback-triage'
import {
  getFeedbackRouteStats,
  listFeedbackRouteRules,
  upsertFeedbackRouteRule,
} from '@/lib/feedback-triage-rpc'
import { REASON_LABELS, reasonLabel } from '@/lib/episode-feedback-triage'

export const Route = createFileRoute('/_authed/feedback-routes')({
  component: FeedbackRoutesPage,
})

const RULES_KEY = ['feedback_route_rules'] as const
const STATS_KEY = ['feedback_route_stats'] as const
const STAT_PAGE_SIZE = 20

interface RuleFormValues {
  source: RouteSource
  keywords: string
  requiresAnimeRef: boolean
  reasons: Array<string>
  isActive: boolean
  note: string
}

function FeedbackRoutesPage() {
  const isMobile = useIsMobile()
  const [editing, setEditing] = useState<RouteRule | 'new' | null>(null)

  const rulesQuery = useQuery({
    queryKey: RULES_KEY,
    queryFn: listFeedbackRouteRules,
  })
  // 默认近 30 天（后端按 Asia/Shanghai 计日）
  const statsQuery = useQuery({
    queryKey: STATS_KEY,
    queryFn: () => getFeedbackRouteStats(null, null),
  })

  const data = rulesQuery.data
  const rules = data?.rules ?? []

  const columns: ColumnsType<RouteRule> = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '方向',
      width: 210,
      render: (_: unknown, row) => ROUTE_SOURCE_LABELS[row.source],
    },
    {
      title: '关键词',
      render: (_: unknown, row) => (
        <Space size={[4, 4]} wrap>
          {row.keywords.map((k) => (
            <Tag key={k} style={{ marginInlineEnd: 0 }}>
              {k}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '附加条件',
      width: 200,
      render: (_: unknown, row) => <RuleCondition row={row} />,
    },
    {
      title: '状态',
      width: 80,
      render: (_: unknown, row) =>
        row.is_active ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>,
    },
    {
      title: '备注',
      width: 180,
      render: (_: unknown, row) =>
        row.note || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Button type="link" size="small" onClick={() => setEditing(row)}>
          编辑
        </Button>
      ),
    },
  ]

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card
        title="反馈分流规则"
        extra={
          <Space size={8}>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => {
                  void rulesQuery.refetch()
                  void statsQuery.refetch()
                }}
                loading={rulesQuery.isFetching && !rulesQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              {isMobile ? '新建' : '新建规则'}
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="用户提交反馈时，命中规则会提示「可能投错入口」，用户可以坚持提交。"
            description={
              isMobile
                ? '开关只能通过后端 migration 修改；关闭时规则不生效。'
                : '规则等 AI 标签数据校准后再加。开关只能通过后端 migration 修改，这里只展示状态；开关关闭时规则不生效。每次保存规则都会递增规则版本并写入审计。'
            }
          />
          {rulesQuery.isError ? (
            <Alert
              type="error"
              showIcon
              message={(rulesQuery.error as Error).message || '规则加载失败'}
            />
          ) : null}
          <Descriptions
            size="small"
            bordered
            column={{ xs: 1, md: 3 }}
            items={[
              {
                label: '功能反馈提交检查',
                children: (
                  <SwitchState on={data?.product_route_check_enabled} />
                ),
              },
              {
                label: '剧集反馈提交检查',
                children: (
                  <SwitchState on={data?.episode_route_check_enabled} />
                ),
              },
              {
                label: '规则版本',
                children: data ? `r${data.rule_revision}` : '—',
              },
            ]}
          />

          <div className="admin-table-wrap">
            <Table<RouteRule>
              rowKey="id"
              size="middle"
              loading={rulesQuery.isLoading}
              columns={columns}
              dataSource={rules}
              scroll={{ x: 1000 }}
              pagination={false}
              locale={{ emptyText: <Empty description="还没有规则" /> }}
            />
          </div>
          <AdminMobileList
            rows={rules}
            rowKey={(row) => row.id}
            loading={rulesQuery.isLoading}
            page={1}
            pageSize={Math.max(rules.length, 1)}
            total={rules.length}
            onPageChange={() => {}}
            emptyContent={
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="还没有规则"
              />
            }
            renderItem={(row) => (
              // 整张卡片即编辑入口
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`编辑规则 #${row.id}`}
                onClick={() => setEditing(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditing(row)
                  }
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">
                    <Typography.Text>
                      #{row.id} · {ROUTE_SOURCE_LABELS[row.source]}
                    </Typography.Text>
                  </div>
                  {row.is_active ? (
                    <Tag color="success">启用</Tag>
                  ) : (
                    <Tag>停用</Tag>
                  )}
                </div>
                <div className="admin-mobile-record__badges">
                  {row.keywords.map((k) => (
                    <Tag key={k}>{k}</Tag>
                  ))}
                </div>
                <div className="admin-mobile-record__meta">
                  <span>
                    <RuleCondition row={row} />
                  </span>
                  {row.note ? <span>备注：{row.note}</span> : null}
                </div>
              </article>
            )}
          />
        </Space>
      </Card>

      <Card title="近 30 天提示统计" size="small">
        <RouteStatsTable
          rows={statsQuery.data ?? []}
          loading={statsQuery.isLoading}
          error={statsQuery.isError ? (statsQuery.error as Error) : null}
        />
      </Card>

      <RuleModal editing={editing} onClose={() => setEditing(null)} />
    </Space>
  )
}

function RuleCondition({ row }: { row: RouteRule }) {
  if (row.source === 'product') {
    return row.requires_anime_ref ? (
      <>需提到番剧</>
    ) : (
      <Typography.Text type="secondary">—</Typography.Text>
    )
  }
  return row.reasons?.length ? (
    <>限原因：{row.reasons.map(reasonLabel).join('、')}</>
  ) : (
    <Typography.Text type="secondary">不限原因</Typography.Text>
  )
}

function SwitchState({ on }: { on: boolean | undefined }) {
  if (on === undefined) return <>—</>
  return on ? <Tag color="success">已开启</Tag> : <Tag>未开启</Tag>
}

function RouteStatsTable({
  rows,
  loading,
  error,
}: {
  rows: Array<RouteStatRow>
  loading: boolean
  error: Error | null
}) {
  const [mobilePage, setMobilePage] = useState(1)
  if (error) {
    return (
      <Alert type="error" showIcon message={error.message || '统计加载失败'} />
    )
  }
  const rowKey = (row: RouteStatRow) =>
    `${row.day}:${row.source}:${row.rule_id}`
  return (
    <>
      <div className="admin-table-wrap">
        <Table<RouteStatRow>
          rowKey={rowKey}
          size="small"
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 30, hideOnSinglePage: true }}
          scroll={{ x: 640 }}
          locale={{ emptyText: <Empty description="暂无提示记录" /> }}
          columns={[
            { title: '日期', dataIndex: 'day', width: 120 },
            {
              title: '方向',
              render: (_: unknown, row) => ROUTE_SOURCE_LABELS[row.source],
            },
            { title: '规则', dataIndex: 'rule_id', width: 80 },
            { title: '提示次数', dataIndex: 'suggested', width: 100 },
            { title: '坚持提交', dataIndex: 'confirmed', width: 100 },
            {
              title: '改道率',
              width: 100,
              render: (_: unknown, row) => {
                const rate = rerouteRate(row.suggested, row.confirmed)
                return rate == null ? '—' : `${Math.round(rate * 100)}%`
              },
            },
          ]}
        />
      </div>
      {/* 手机：按日 × 规则的紧凑卡片，数字一行排开 */}
      <AdminMobileList
        rows={rows.slice(
          (mobilePage - 1) * STAT_PAGE_SIZE,
          mobilePage * STAT_PAGE_SIZE,
        )}
        rowKey={rowKey}
        loading={loading}
        page={mobilePage}
        pageSize={STAT_PAGE_SIZE}
        total={rows.length}
        onPageChange={setMobilePage}
        emptyContent={
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={loading ? '加载中…' : '暂无提示记录'}
          />
        }
        renderItem={(row) => {
          const rate = rerouteRate(row.suggested, row.confirmed)
          return (
            <article className="admin-mobile-record feedback-routes-stat">
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  <Typography.Text>
                    {row.day} · 规则 #{row.rule_id}
                  </Typography.Text>
                  <div className="admin-mobile-record__meta">
                    <span>{ROUTE_SOURCE_LABELS[row.source]}</span>
                  </div>
                </div>
                <Tag>
                  {rate == null ? '—' : `改道 ${Math.round(rate * 100)}%`}
                </Tag>
              </div>
              <div className="admin-mobile-record__stats">
                <span>提示 {row.suggested}</span>
                <span>坚持提交 {row.confirmed}</span>
              </div>
            </article>
          )
        }}
      />
    </>
  )
}

function RuleModal({
  editing,
  onClose,
}: {
  editing: RouteRule | 'new' | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm<RuleFormValues>()
  const source = Form.useWatch('source', form)
  const rule = editing && editing !== 'new' ? editing : null

  const initialValues = useMemo<RuleFormValues>(
    () => ({
      source: rule?.source ?? 'product',
      keywords: rule?.keywords.join('、') ?? '',
      requiresAnimeRef: rule?.requires_anime_ref ?? false,
      reasons: rule?.reasons ?? [],
      isActive: rule?.is_active ?? true,
      note: rule?.note ?? '',
    }),
    [rule],
  )

  const mutation = useMutation({
    mutationFn: (v: RuleFormValues) =>
      upsertFeedbackRouteRule({
        id: rule?.id ?? null,
        source: v.source,
        keywords: splitKeywords(v.keywords),
        requiresAnimeRef: v.requiresAnimeRef,
        reasons: v.reasons,
        isActive: v.isActive,
        note: v.note,
      }),
    onSuccess: () => {
      message.success(rule ? '规则已更新' : '规则已创建')
      qc.invalidateQueries({ queryKey: RULES_KEY })
      onClose()
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  return (
    <Modal
      open={editing !== null}
      title={rule ? `编辑规则 #${rule.id}` : '新建规则'}
      onCancel={onClose}
      onOk={() => form.submit()}
      okButtonProps={{ loading: mutation.isPending }}
      destroyOnHidden
    >
      <Form<RuleFormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={(v) => mutation.mutate(v)}
        preserve={false}
      >
        <Form.Item name="source" label="方向" required>
          <Select
            options={(
              Object.entries(ROUTE_SOURCE_LABELS) as Array<
                [RouteSource, string]
              >
            ).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>
        <Form.Item
          name="keywords"
          label="关键词"
          required
          extra="用逗号、顿号或换行分隔；包含匹配（不是正则），最多 50 个，每个 ≤32 字"
          rules={[
            {
              validator: (_, value: string) =>
                splitKeywords(value ?? '').length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('至少填一个关键词')),
            },
          ]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>
        {source === 'episode' ? (
          <Form.Item name="reasons" label="限定原因" extra="不选 = 不限原因">
            <Select
              mode="multiple"
              allowClear
              options={Object.entries(REASON_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="requiresAnimeRef"
            label="需提到具体番剧"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        )}
        <Form.Item name="isActive" label="启用" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="note" label="备注">
          <Input maxLength={200} showCount />
        </Form.Item>
      </Form>
      {rule?.updated_at ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          上次修改 {formatCST(rule.updated_at)}
        </Typography.Text>
      ) : null}
    </Modal>
  )
}
